import * as cs from "../cs/cs.mjs";

////
// Contains handling of submitting the form.
//
// Note that the programmatic "up.render" is used to
// - be more flexible when to run,
// - to send context info via headers,
// - to run onFinished hook,
// - and more.
//
// Note that "up.validate" and "up.submit" are not used as both cannot replace
// fragments in multiple targets (at least with 3.10.1). Their behavior is
// represented with "up.render".
//
// Note that `reason` must match the serverside code.
//
const Form = (function () {
  "use strict";

  const config = {
    form: '.js-boat-form',
    norepContainer: '.js-non-replaceable-container',
    fieldContainer: '.field',
    fieldWithError: '[data-cs-valid="false"]:not(span)',
    saveButton: '.js-save-button'
  }

  let isValidating = false;

  // The event listeners are added to a DOM element outside the replaced HTML,
  // hence no re-enabling after replacement is necessary.
  const init = function (options = {}) {
    Object.assign(config, options);
    cs.q(config.norepContainer).on('click', config.saveButton, onSaveOffer);
    addFieldValidationListeners();
    addFieldUpdateListeners();
  };

  // Registers a listener for each field with matching selector.
  // The listener is added on a parent element outside of the replace fragments.
  // Hence, re-adding listener after replacement is not necessary and later
  // added fields are considered.
  // The code is run only if the current event type matches the defined event
  // type of the field. For example, a `data-validate-on="change"` for a text
  // field will run the validation on event `change` but not `blur`.
  const addFieldValidationListeners = function (e) {
    cs.q(config.norepContainer).on('change', '[data-validate-on]', onValidateField);
    cs.q(config.norepContainer).on('focusout', '[data-validate-on]', onValidateField);
  };

  const addFieldUpdateListeners = function (e) {
    cs.q(config.norepContainer).on('change', '[data-update-on]', onUpdateField);
    cs.q(config.norepContainer).on('focusout', '[data-update-on]', onUpdateField);
  };

  const onValidateField = function (e) {
    const validateOn = (e.target.getAttribute('data-validate-on') || '').split(' ');
    if (!validateOn.includes(e.type)) return;
    if (isValidating) return;

    // On focusout on field A, the form is submitted, and the browser sets the
    // focus to field B. When field A has an error, it is inserted into the page
    // and focused (see below). This moves the cursor from field B to field A.
    // This triggers a focusout on field B which triggers a form submission,
    // and this is an endless loop. To avoid this, we set a guard flag here.
    isValidating = true;

    submitForm(
      e,
      {
        reason: 'validate_field',
        target: e.target.getAttribute('data-validate-target') || `${config.fieldContainer}`,
        onStarted: function () {},
        onFinished: function () { isValidating = false; },
        focus: function (fragment) {
          if (fragment) {
            const field = fragment.querySelector(config.fieldWithError)
            if (field) {
              up.focus(field)
            }
          }
        }
      }
    );
  }

  // This might be better named "onUpdateFragment"
  const onUpdateField = function (e) {
    const updateOn = (e.target.getAttribute('data-update-on') || '').split(' ');
    if (!updateOn.includes(e.type)) return;

    submitForm(
      e,
      {
        reason: 'update_field',
        target: e.target.getAttribute('data-update-target') || `${config.fieldContainer}`,
        onStarted: function () {},
        onFinished: function () {},
        focus: function (fragment) {
          if (fragment) {
            const focusableElements = Array.from(
              fragment.querySelectorAll('input[type="text"], textarea')
            ).filter(el => !el.disabled && !el.hidden);

            if (focusableElements[0]) {
              up.focus(focusableElements[0])
            }
          }
        }
      }
    );
  };

  const onSaveOffer = function (e) {
    e.preventDefault();

    submitForm(
      e,
      {
        reason: 'save',
        target: `${config.form}`,
        onStarted: function () {
          cs.q(config.saveButton).addClass('is-loading');
        },
        onFinished: function () {
          cs.q(config.saveButton).removeClass('is-loading');
        },
        focus: function () {}
      }
    )
  };

  const submitForm = function (e, context) {
    const field = e.target,
      fieldName = field.getAttribute('name'),
      // The save/report button is outside the form, hence no `field.closest('form')`
      form = document.querySelector(config.form),
      formId = form.getAttribute('id'),
      formUrl = form.getAttribute('action'),
      // formMethod = form.querySelector('[name="_method"]').getAttribute('value'),
      fieldsWithError = form.querySelectorAll(config.fieldWithError);
    let formParams;

    let formMethod = form.querySelector('[name="_method"]');
    if (formMethod === null) {
      formMethod = 'POST';
    } else {
      formMethod = formMethod.getAttribute('value');
    }

    formParams = up.Params.fromForm(`#${formId}`)
    formParams = formParams.toArray();

    context.onStarted();

    // Update render timestamp needed for Capybara tests (cs_capybara)
    context.target = `${context.target}, #cy-render-timestamp`

    up.render(
      {
        url: formUrl, method: formMethod, params: formParams,
        headers: {
          'X-CS-FORM-EVENT': context.reason,
          'X-CS-FORM-FIELD': fieldName,
          'X-CS-FORM-ERROR-COUNT': fieldsWithError.length
        },
        target: context.target,
        // We have multiple fields with a parent container `config.fieldContainer`
        // Ensure that the closest fragment is replaced, not the first.
        origin: e.target, match: 'region',
        onFinished: context.onFinished,
        focus: context.focus
      }
    )
  };

  return {init};
})();

export default Form;
